Vue.asyncComponent('cms-dashboard-checks-accordion', {
    props: {
        widgets: {
            type: Array,
            default: () => [],
        },
        activeTabId: {
            type: String,
            default: '',
        },
        callbackUri: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            expandedWidgetId: null,
        };
    },
    computed: {
        accordionWidget() {
            return this.widgets.find((widget) => widget?.type === 'cms-dashboard-checks-accordion') || null;
        },
        childWidgetReferenceIds() {
            const referenceIds = this.getWidgetAttributes(this.accordionWidget).childWidgetReferenceIds;

            return Array.isArray(referenceIds)
                ? referenceIds.map((referenceId) => String(referenceId || '').trim()).filter(Boolean)
                : [];
        },
        checkWidgets() {
            const configuredReferenceIds = new Set(this.childWidgetReferenceIds);
            const filterConfiguredWidgets = configuredReferenceIds.size > 0;

            return this.widgets
                .filter((widget) => this.isCheckWidget(widget))
                .filter((widget) => !filterConfiguredWidgets || configuredReferenceIds.has(this.getWidgetReferenceId(widget)))
                .sort((leftWidget, rightWidget) => {
                    const severityDifference = this.getSeverityWeight(this.getWidgetSeverityKey(leftWidget))
                        - this.getSeverityWeight(this.getWidgetSeverityKey(rightWidget));

                    if (severityDifference !== 0) {
                        return severityDifference;
                    }

                    return this.getWidgetDisplayTitle(leftWidget).localeCompare(this.getWidgetDisplayTitle(rightWidget));
                });
        },
        severityGroups() {
            const groupDefinitions = [
                { key: 'critical', title: this.$t('Fouten') },
                { key: 'warning', title: this.$t('Aandachtspunten') },
                { key: 'success', title: this.$t('Ok / actueel') },
            ];

            return groupDefinitions.map((groupDefinition) => {
                const widgets = this.checkWidgets.filter((widget) => this.getWidgetSeverityKey(widget) === groupDefinition.key);

                return {
                    ...groupDefinition,
                    count: widgets.length,
                    widgets,
                };
            }).filter((group) => group.widgets.length > 0);
        },
    },
    methods: {
        getWidgetAttributes(widget) {
            return widget?.attributes && typeof widget.attributes === 'object'
                ? widget.attributes
                : {};
        },
        isCheckWidget(widget) {
            return widget && typeof widget === 'object' && ![
                'cms-dashboard-checks-accordion',
                'cms-dashboard-title',
                'cms-dashboard-issues-summary',
                'cms-dashboard-shortcut',
                'cms-dashboard-profile',
            ].includes(widget.type);
        },
        getWidgetReferenceId(widget) {
            return String(widget?.attributes?.referenceId || widget?.referenceId || widget?.id || '');
        },
        getWidgetSeverityKey(widget) {
            const attributes = this.getWidgetAttributes(widget);

            if (this.getWidgetCriticalIssueCount(widget) > 0) {
                return 'critical';
            }

            if (this.getWidgetIssueCount(widget) > 0) {
                return 'warning';
            }

            if (this.activeTabId === 'content-activity' && Array.isArray(attributes.items) && attributes.items.length > 0) {
                return 'warning';
            }

            return 'success';
        },
        getSeverityWeight(severity) {
            return ({ critical: 0, warning: 1, success: 2 })[severity] ?? 99;
        },
        getWidgetIssueCount(widget) {
            const attributes = this.getWidgetAttributes(widget);
            const issueCount = Number(attributes.issueCount);

            if (Number.isFinite(issueCount)) {
                return Math.max(issueCount, 0);
            }

            return attributes.statusLabel && attributes.isEnabled ? 1 : 0;
        },
        getWidgetCriticalIssueCount(widget) {
            const criticalIssueCount = Number(this.getWidgetAttributes(widget).criticalIssueCount);

            return Number.isFinite(criticalIssueCount) ? Math.max(criticalIssueCount, 0) : 0;
        },
        getWidgetDisplayTitle(widget) {
            const attributes = this.getWidgetAttributes(widget);

            return attributes.statusLabel || attributes.title || widget?.type || '';
        },
        getWidgetStatusLabel(widget) {
            const attributes = this.getWidgetAttributes(widget);

            return String(attributes.statusValue || attributes.value || '').trim();
        },
        getWidgetStatusChipColor(widget) {
            return ({ critical: 'danger', warning: 'warning', success: 'success' })[this.getWidgetSeverityKey(widget)];
        },
        getWidgetShortDescription(widget) {
            const attributes = this.getWidgetAttributes(widget);

            return String(attributes.statusDescription || attributes.description || '').trim();
        },
        getWidgetListIcon(widget) {
            const attributes = this.getWidgetAttributes(widget);
            const iconByType = {
                'cms-dashboard-debug-status': 'bug_report',
                'cms-dashboard-email-debug-status': 'email',
                'cms-dashboard-display-errors-status': 'error',
                'cms-dashboard-www-redirect-check': 'language',
                'cms-dashboard-http-to-https-redirect-check': 'https',
                'cms-dashboard-site-files-check': 'folder',
                'cms-dashboard-favicon-check': 'image',
                'cms-dashboard-ssl-certificate-expiry-check': 'verified_user',
                'cms-dashboard-seo-unique-title-tags': 'title',
                'cms-dashboard-seo-meta-descriptions': 'description',
                'cms-dashboard-seo-social-meta-tags': 'share',
                'cms-dashboard-page-status-check': 'find_in_page',
                'cms-dashboard-image-alt-tags-check': 'image_search',
                'cms-dashboard-heading-structure-check': 'format_list_numbered',
                'cms-dashboard-dead-links-check': 'link_off',
                'cms-dashboard-spine-version': 'deployed_code',
                'cms-dashboard-queue-workers': 'account_tree',
                'cms-dashboard-upcoming-pages-online': 'calendar_today',
                'cms-dashboard-upcoming-pages-offline': 'event_busy',
                'cms-dashboard-upcoming-publications-online': 'event_available',
                'cms-dashboard-upcoming-publications-offline': 'event_busy',
                'cms-dashboard-concept-pages': 'edit_note',
            };

            return attributes.icon || iconByType[widget?.type] || 'checklist';
        },
        getWidgetRenderAttributes(widget) {
            return {
                ...this.getWidgetAttributes(widget),
                id: widget.id,
            };
        },
        toggleWidget(widget) {
            const widgetId = this.getWidgetReferenceId(widget);

            this.expandedWidgetId = this.expandedWidgetId === widgetId ? null : widgetId;
        },
        isWidgetExpanded(widget) {
            return this.expandedWidgetId === this.getWidgetReferenceId(widget);
        },
        handleWidgetInput(widgetId, value) {
            this.$emit('widget-input', widgetId, value);
        },
    },
}, 'cms/dashboard/live/cms-dashboard-checks-accordion.html');
